import { useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";

/**
 * Automatically checks file checkboxes of a given file_type (default "riboseq")
 * If a queryParam (such as 'files') is passed it will first check if that queryParam
 * is present and if so will not check anything by default (this is currently only
 * applicable on sucodon profile plot)

 * @param {Object} params
 * @param {Object} params.apiData - data fetched from the initial page load, contains 'json_files'
 * @param {(ids: string[]) => void} params.selectMultipleFiles - Setter to select multiple IDs
 * @param {string} [params.fileType="riboseq"] - File type to auto-select
 * @param {string} [params.queryParam=null] - Query param that disables defaults

 */
export function useDefaultFileSelection({
  apiData,
  selectMultipleFiles,
  fileType = "riboseq",
  queryParam = null,
}) {
  const location = useLocation();
  // Derive the target file_ids for files with target fileType
  const targetIds = useMemo(() => {
    const files = (apiData?.json_files ?? []).filter(
      (f) => f.file_type === fileType
    );
    return files.map((f) => f.file_id);
  }, [apiData, fileType]);

  useEffect(() => {
     // If files are passed in urlParams do not check anything by default, this applies
     // to subcodon plots when other plots link to it, not to be confused
     // with short codes being passed which is a separate thing.
     if (queryParam) {
       const params = new URLSearchParams(location.search);
       if (params.get(queryParam)) return;
     }

    // If no file ids with relevant file type, exit
    if (!targetIds.length) {
      return;
    }

    // If a short code is passed, don't check anything
    if (location.state) {
      if (location.state?.suppressDefaults) {
        return;
      }
    }
 
    // Send target file ids to selectMultipleFiles to check them
    selectMultipleFiles(targetIds);
  }, [location.search, 
      location.state?.suppressDefaults,
      queryParam, 
      targetIds, 
      selectMultipleFiles]);
}
